/**
 * Algolia Filters Enhancement
 * Improves Algolia filters UX with immediate opening and auto-scroll
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    scrollDuration: 300, // Reduced from 500ms to 300ms for faster scroll
    scrollOffset: 100, // Offset from top for better visibility
    focusDelay: 50, // Reduced for faster response
    selectors: {
      page: '.ais-page',
      facets: '.ais-facets',
      header: '.ais-range-slider--header, .ais-facet--header, .ais-header',
      dropdown: '.ais-facet-dropdown-container',
      wrapper: '.ais-facet-dropdown-wrapper',
      checkbox: '.ais-dropdown-checkbox',
      refinementList: '.ais-RefinementList',
      facetLabel: '.ais-facet--label'
    }
  };

  // Application state
  const state = {
    currentOpenDropdown: null,
    isAnimating: false
  };

  /**
   * Main initialization
   */
  function init() {
    // Wait for Algolia to be ready
    waitForAlgolia(() => {
      setupFilterEnhancements();
      setupAccessibility();
      setupMobileOptimizations();
      observeDOMChanges();
    });
  }

  /**
   * Wait for Algolia to be fully loaded
   */
  function waitForAlgolia(callback) {
    const checkInterval = setInterval(() => {
      const facets = document.querySelector(CONFIG.selectors.facets);
      const headers = document.querySelectorAll(CONFIG.selectors.header);
      
      if (facets && headers.length > 0) {
        clearInterval(checkInterval);
        callback();
      }
    }, 100);
    
    // Safety timeout
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  /**
   * Setup filter enhancements
   */
  function setupFilterEnhancements() {
    // Remove previous listeners if they exist
    document.removeEventListener('click', handleFilterClick, true);
    
    // Add new listener with capture
    document.addEventListener('click', handleFilterClick, true);
    
    // Configure each header individually
    const headers = document.querySelectorAll(CONFIG.selectors.header);
    headers.forEach(header => {
      // Mark as enhanced to avoid duplicates
      if (!header.dataset.enhanced) {
        header.dataset.enhanced = 'true';
        
        // Improve accessibility
        header.setAttribute('role', 'button');
        header.setAttribute('tabindex', '0');
        header.style.cursor = 'pointer';
        
        // Add visual indicator of interactivity
        header.addEventListener('mouseenter', () => {
          if (!state.isAnimating) {
            header.style.opacity = '0.8';
          }
        });
        
        header.addEventListener('mouseleave', () => {
          header.style.opacity = '1';
        });
      }
    });
  }

  /**
   * Handles click on filter headers
   */
  function handleFilterClick(event) {
    const header = event.target.closest(CONFIG.selectors.header);
    
    if (!header) return;
    
    // Prevent default behavior
    event.preventDefault();
    event.stopPropagation();
    
    const wrapper = header.closest(CONFIG.selectors.wrapper);
    if (!wrapper) return;
    
    const dropdown = wrapper.querySelector(CONFIG.selectors.dropdown);
    const checkbox = wrapper.querySelector(CONFIG.selectors.checkbox);
    
    if (!dropdown) return;
    
    // Determine if dropdown is open
    const isOpen = checkbox ? checkbox.checked : dropdown.style.display !== 'none';
    
    if (!isOpen) {
      // Open dropdown immediately
      openDropdown(wrapper, dropdown, checkbox, header);
    } else {
      // Close dropdown
      closeDropdown(wrapper, dropdown, checkbox, header);
    }
  }

  /**
   * Opens a dropdown with animation and scroll
   */
  function openDropdown(wrapper, dropdown, checkbox, header) {
    // Close other dropdowns IMMEDIATELY before any other action
    closeOtherDropdowns(wrapper);
    
    if (state.isAnimating) return;
    
    state.isAnimating = true;
    
    // Mark checkbox as checked if it exists
    if (checkbox) {
      checkbox.checked = true;
    }
    
    // Show dropdown immediately
    dropdown.style.display = 'block';
    dropdown.style.opacity = '0';
    dropdown.style.maxHeight = '0';
    
    // Force reflow
    dropdown.offsetHeight;
    
    // Animate opening
    requestAnimationFrame(() => {
      dropdown.style.transition = 'opacity 0.2s ease, max-height 0.2s ease'; // Faster animation
      dropdown.style.opacity = '1';
      dropdown.style.maxHeight = dropdown.scrollHeight + 'px';
      
      // Update aria
      header.setAttribute('aria-expanded', 'true');
      wrapper.classList.add('is-open');
      
      // Auto-scroll to ensure visibility
      setTimeout(() => {
        ensureDropdownVisibility(dropdown, wrapper);
        state.isAnimating = false;
        state.currentOpenDropdown = wrapper;
        
        // Focus on first interactive element
        focusFirstElement(dropdown);
      }, 200); // Reduced to sync with faster scroll
    });
  }

  /**
   * Closes a dropdown with animation
   */
  function closeDropdown(wrapper, dropdown, checkbox, header) {
    if (state.isAnimating) return;
    
    state.isAnimating = true;
    
    // Animate closing
    dropdown.style.transition = 'opacity 0.2s ease, max-height 0.2s ease'; // Faster
    dropdown.style.opacity = '0';
    dropdown.style.maxHeight = '0';
    
    setTimeout(() => {
      dropdown.style.display = 'none';
      
      if (checkbox) {
        checkbox.checked = false;
      }
      
      header.setAttribute('aria-expanded', 'false');
      wrapper.classList.remove('is-open');
      
      state.isAnimating = false;
      
      if (state.currentOpenDropdown === wrapper) {
        state.currentOpenDropdown = null;
      }
    }, 200); // Reduced to sync with faster animation
  }

  /**
   * Cierra otros dropdowns abiertos
   */
  function closeOtherDropdowns(currentWrapper) {
    if (!currentWrapper) return;
    
    const allWrappers = document.querySelectorAll(CONFIG.selectors.wrapper);
    
    allWrappers.forEach(wrapper => {
      if (wrapper !== currentWrapper) {
        const dropdown = wrapper.querySelector(CONFIG.selectors.dropdown);
        const checkbox = wrapper.querySelector(CONFIG.selectors.checkbox);
        const header = wrapper.querySelector(CONFIG.selectors.header);
        
        // Close immediately without checking if open
        if (dropdown && (wrapper.classList.contains('is-open') || checkbox?.checked)) {
          // Immediate close without animation for other dropdowns
          dropdown.style.transition = 'none';
          dropdown.style.display = 'none';
          dropdown.style.opacity = '0';
          dropdown.style.maxHeight = '0';
          
          if (checkbox) {
            checkbox.checked = false;
          }
          
          if (header) {
            header.setAttribute('aria-expanded', 'false');
          }
          
          wrapper.classList.remove('is-open');
        }
      }
    });
    
    // Reset current dropdown state
    state.currentOpenDropdown = null;
  }

  /**
   * Asegura que el dropdown sea completamente visible
   */
  function ensureDropdownVisibility(dropdown, wrapper) {
    const facetsContainer = document.querySelector(CONFIG.selectors.facets);
    if (!facetsContainer) return;
    
    // Obtener posiciones
    const dropdownRect = dropdown.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const facetsRect = facetsContainer.getBoundingClientRect();
    
    // Calcular si necesita scroll
    const dropdownBottom = dropdownRect.bottom;
    const facetsBottom = facetsRect.bottom;
    const viewportHeight = window.innerHeight;
    
    // If dropdown extends beyond viewport or facets container
    if (dropdownBottom > viewportHeight || dropdownBottom > facetsBottom) {
      // Calculate target scroll position
      const targetScrollTop = wrapperRect.top + window.pageYOffset - CONFIG.scrollOffset;
      
      // If inside a scrollable container
      if (facetsContainer.scrollHeight > facetsContainer.clientHeight) {
        // Scroll within facets container
        const containerScrollTarget = wrapper.offsetTop - CONFIG.scrollOffset;
        smoothScrollElement(facetsContainer, containerScrollTarget, CONFIG.scrollDuration);
      } else {
        // Page scroll
        smoothScrollTo(targetScrollTop, CONFIG.scrollDuration);
      }
    }
    
    // If header is out of view above
    if (wrapperRect.top < 0) {
      const targetScrollTop = wrapperRect.top + window.pageYOffset - CONFIG.scrollOffset;
      smoothScrollTo(targetScrollTop, CONFIG.scrollDuration);
    }
  }

  /**
   * Smooth scroll for a specific element
   */
  function smoothScrollElement(element, targetPosition, duration) {
    const startPosition = element.scrollTop;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();
    
    function animation(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeInOutCubic = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      element.scrollTop = startPosition + distance * easeInOutCubic;
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    }
    
    requestAnimationFrame(animation);
  }

  /**
   * Scroll suave de la ventana
   */
  function smoothScrollTo(targetPosition, duration) {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();
    
    function animation(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function
      const easeInOutCubic = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      window.scrollTo(0, startPosition + distance * easeInOutCubic);
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    }
    
    requestAnimationFrame(animation);
  }

  /**
   * Sets focus on first interactive element
   */
  function focusFirstElement(dropdown) {
    setTimeout(() => {
      const focusableElements = dropdown.querySelectorAll(
        'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }, CONFIG.focusDelay);
  }

  /**
   * Configura accesibilidad mejorada
   */
  function setupAccessibility() {
    // Keyboard navigation
    document.addEventListener('keydown', (event) => {
      const header = event.target.closest(CONFIG.selectors.header);
      
      if (header) {
        switch(event.key) {
          case 'Enter':
          case ' ':
            event.preventDefault();
            header.click();
            break;
          case 'Escape':
            const wrapper = header.closest(CONFIG.selectors.wrapper);
            if (wrapper && wrapper.classList.contains('is-open')) {
              header.click();
            }
            break;
        }
      }
    });
  }

  /**
   * Mobile optimizations
   */
  function setupMobileOptimizations() {
    if (!isMobile()) return;
    
    // Improve touch response
    const headers = document.querySelectorAll(CONFIG.selectors.header);
    
    headers.forEach(header => {
      // Prevent click delay on mobile
      header.style.touchAction = 'manipulation';
      
      // Immediate visual feedback
      header.addEventListener('touchstart', () => {
        header.classList.add('touch-active');
      }, { passive: true });
      
      header.addEventListener('touchend', () => {
        setTimeout(() => {
          header.classList.remove('touch-active');
        }, 100);
      }, { passive: true });
    });
    
    // Adjust scroll offset for mobile
    if (window.innerWidth < 768) {
      CONFIG.scrollOffset = 60;
    }
  }

  /**
   * Detects if it's a mobile device
   */
  function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  /**
   * Observes DOM changes for dynamically added elements
   */
  function observeDOMChanges() {
    const observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && (
              node.matches(CONFIG.selectors.wrapper) ||
              node.querySelector && node.querySelector(CONFIG.selectors.wrapper)
            )) {
              shouldReinitialize = true;
            }
          }
        });
      });
      
      if (shouldReinitialize) {
        setTimeout(setupFilterEnhancements, 100);
      }
    });
    
    const targetNode = document.querySelector(CONFIG.selectors.facets) || document.body;
    observer.observe(targetNode, {
      childList: true,
      subtree: true
    });
  }

  // CSS adicional para mejorar la experiencia
  function injectStyles() {
    const style = document.createElement('style');
    style.textContent = `
      /* Visual improvements for filters */
      .ais-facet-dropdown-wrapper {
        transition: background-color 0.2s ease;
      }
      
      .ais-facet-dropdown-wrapper.is-open {
        border-radius: 4px;
        padding: 8px;
        margin-bottom: 12px;
      }
      
      .ais-facet-dropdown-container {
        overflow: hidden;
      }
      
      .ais-range-slider--header,
      .ais-facet--header,
      .ais-header {
        user-select: none;
        transition: opacity 0.2s ease;
      }
      
      .ais-range-slider--header.touch-active,
      .ais-facet--header.touch-active,
      .ais-header.touch-active {
        background-color: rgba(93, 113, 137, 0.1);
      }
      
      /* Plus/Minus indicator */
      .ais-facet-dropdown-wrapper .ais-header::after {
        content: "+";
        float: right;
        font-size: 18px;
        font-weight: bold;
        color: #5d7189;
        transition: all 0.3s ease;
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
      
      .ais-facet-dropdown-wrapper.is-open .ais-header::after {
        content: "−";
      }
      
      /* Smooth scrolling for facets container */
      .ais-facets {
        scroll-behavior: smooth;
      }
      
      /* Visual improvement during animation */
      @media (prefers-reduced-motion: reduce) {
        .ais-facet-dropdown-container,
        .ais-facets {
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      injectStyles();
      init();
    });
  } else {
    injectStyles();
    init();
  }

  // Expose public API for debugging
  window.AlgoliaFiltersEnhancement = {
    init,
    openDropdown,
    closeDropdown,
    getState: () => state,
    CONFIG
  };

})();